import {
  ActionIcon,
  Anchor,
  Burger,
  Flex,
  Group,
  Tooltip,
  useMantineColorScheme
} from '@mantine/core'
import { IconMoon, IconSun } from '@tabler/icons-react'
import { FormattedMessage } from 'react-intl'
import { Link } from 'react-router'
import { NotificationDropdown } from '@/components/Notifications/NotificationDropdown'
import { VpLogo } from '@/components/ui-shared/VpLogo/VpLogo'
import { WorkspaceSelectHandler } from '@/components/workspaces/WorkspaceSelect/WorkspaceSelectHandler'
import { useDeviceDetect } from '@/hooks/useDeviceDetect'
import { RouterPath } from '@/router/paths'
import { UserMenu } from './UserMenu'

export const HEADER_HEIGHT = 60

const LOGO_WIDTH = 200

type DarkModeToggleProps = {
  isDarkMode: boolean
  onClick: () => void
}

const DarkModeToggle = ({ isDarkMode, onClick }: DarkModeToggleProps) => {
  return (
    <Tooltip label={<FormattedMessage id="header.toolbar.toggleTheme" />}>
      <ActionIcon
        variant="subtle"
        color="gray.0"
        size="sm"
        radius="xl"
        onClick={onClick}
      >
        {isDarkMode ? (
          <IconSun style={{ width: '80%', height: '80%' }} />
        ) : (
          <IconMoon style={{ width: '80%', height: '80%' }} />
        )}
      </ActionIcon>
    </Tooltip>
  )
}

type HeaderProps = {
  isMenuOpen: boolean
  onBurgerMenuClick: () => void
}

export const Header = ({ isMenuOpen, onBurgerMenuClick }: HeaderProps) => {
  const { isMobile } = useDeviceDetect()
  const { colorScheme, toggleColorScheme } = useMantineColorScheme()
  const isDarkMode = colorScheme === 'dark'

  return (
    <Flex align="center" justify="space-between" h="100%">
      <Group gap={isMobile ? 'sm' : 'xl'} wrap="nowrap">
        {isMobile ? (
          <Burger
            color="white"
            size="xs"
            opened={isMenuOpen}
            onClick={onBurgerMenuClick}
          />
        ) : (
          <Anchor component={Link} to={RouterPath.root}>
            <VpLogo width={LOGO_WIDTH} variant="light" />
          </Anchor>
        )}

        <WorkspaceSelectHandler
          selectProps={{
            size: isMobile ? 'xs' : 'sm',
            miw: isMobile ? 160 : 220,
            comboboxProps: {
              width: 320,
              position: 'bottom-start',
              withOverlay: true
            },
            styles: (theme) => ({
              input: {
                backgroundColor: theme.colors['brand-primary'][4],
                borderColor: theme.colors['brand-primary'][1],
                color: theme.colors.gray[0]
              },
              option: {
                padding: theme.spacing.sm
              }
            })
          }}
        />
      </Group>

      <Group gap="md" wrap="nowrap" align="center">
        <NotificationDropdown />

        <DarkModeToggle isDarkMode={isDarkMode} onClick={toggleColorScheme} />

        <UserMenu />
      </Group>
    </Flex>
  )
}
